import { useEffect, useState } from "react";
import { api, type ContainerDetail, type ContainerTask } from "../api.ts";
import { captionStyle, colors, serif, shortDate, tokens } from "../theme.ts";
import { BottomSheet } from "./BottomSheet.tsx";

/** The word for a container's kind, as it is shown. */
export const KIND_LABEL = { case: "case", pursuit: "pursuit", project: "project" } as const;

/** "case · Return drafted · waiting on the Steuerberater": the one line a container is shown by. */
export function containerLine(c: { kind: keyof typeof KIND_LABEL; status_line: string | null; waiting_on: string | null; lifecycle?: string }) {
  return [
    KIND_LABEL[c.kind],
    // "waiting" is left to the waiting-on line when there is one, so it is not said twice.
    c.lifecycle && c.lifecycle !== "active" && !(c.lifecycle === "waiting" && c.waiting_on) ? c.lifecycle : null,
    c.status_line,
    c.waiting_on ? `waiting on ${c.waiting_on}` : null,
  ].filter(Boolean).join(" · ");
}

/** 14 Oct, 09:30 for an appointment; the date for a dated task; nothing otherwise. */
const whenOf = (t: ContainerTask) => {
  if (t.at) return `${shortDate(t.at.slice(0, 10))}, ${t.at.slice(11, 16)}`;
  return shortDate(t.due);
};

/**
 * A container's detail: its status line, what it waits on, its open tasks and
 * its context path. Read-only: containers are changed through the MCP tools,
 * and a task opens in its own sheet. The open tasks are listed, never counted.
 */
export function ContainerSheet({
  containerKey, onClose, onOpenTask,
}: { containerKey: string; onClose: () => void; onOpenTask: (id: string) => void }) {
  const [c, setC] = useState<ContainerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.container(containerKey).then((x) => live && setC(x)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [containerKey]);

  return (
    <BottomSheet onClose={onClose}>
      {error && <p style={{ margin: 0, fontSize: 14, color: colors.ink }}>{error}</p>}
      {!c && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}
      {c && (
        <div>
          <div style={captionStyle}>{[c.area_name, c.key].join(" · ")}</div>
          <div style={{
            fontFamily: serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight,
            color: colors.ink, margin: "6px 0 4px",
          }}>{c.name}</div>
          <div style={{ fontSize: 13, color: colors.ink2 }}>
            {KIND_LABEL[c.kind]}{c.lifecycle !== "active" ? ` · ${c.lifecycle}` : ""}
          </div>

          <Field label="Status">{c.status_line ?? "None written yet."}</Field>
          {c.waiting_on && <Field label="Waiting on">{c.waiting_on}</Field>}

          <div style={{ ...captionStyle, marginTop: 18 }}>Open tasks</div>
          {c.open_tasks.length === 0 && <p style={{ margin: "6px 0 0", fontSize: 15, color: colors.ink2 }}>Nothing for you here right now.</p>}
          {c.open_tasks.map((t) => (
            <div
              key={t.id} role="button" tabIndex={0}
              onClick={() => onOpenTask(t.id)}
              onKeyDown={(e) => { if (e.key === "Enter") onOpenTask(t.id); }}
              style={{
                display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center",
                padding: "12px 0", borderBottom: `1px solid ${colors.hair}`, cursor: "pointer",
              }}
            >
              <div style={{ minWidth: 0, lineHeight: 1.3 }}>
                <div style={{ fontSize: 15, color: t.state === "waiting" ? colors.ink2 : colors.ink }}>{t.title}</div>
                <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>
                  {[t.id, t.state === "waiting" ? "waiting" : null, t.needs_triage ? "to sort" : null].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div style={{ fontSize: 13, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>{whenOf(t)}</div>
            </div>
          ))}

          {c.context_path && <Field label="Context">{c.context_path}</Field>}
        </div>
      )}
    </BottomSheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={captionStyle}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 15, lineHeight: 1.4, color: colors.ink, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}
